/**
 * <at-partner-logos-marquee>
 *
 * Progressive enhancement on top of the pure-CSS marquee in
 * assets/at-partner-logos.css. The animation runs and loops correctly with
 * this script disabled or failing to load — everything here only *pauses*
 * an animation that's already running:
 *
 * - Pauses while scrolled off-screen (IntersectionObserver) to save CPU/
 *   battery on a continuously-running animation the visitor can't see.
 * - Pauses on pointer/keyboard focus as a fallback alongside the CSS
 *   `:hover { animation-play-state: paused }` rule (touch devices and some
 *   browsers don't reliably surface `:hover` on custom elements).
 * - Mirrors `prefers-reduced-motion: reduce` in JS as a belt-and-suspenders
 *   check alongside the CSS `@media (prefers-reduced-motion: reduce)` rule,
 *   and reacts live if the visitor changes that OS setting mid-session.
 */
class PartnerLogosMarquee extends HTMLElement {
  #controller = new AbortController();
  #intersectionObserver;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#syncMotionPreference();
    this.#motionQuery.addEventListener('change', this.#syncMotionPreference, {
      signal: this.#controller.signal,
    });

    this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection);
    this.#intersectionObserver.observe(this);

    const { signal } = this.#controller;
    this.addEventListener('pointerenter', this.#pause, { signal });
    this.addEventListener('pointerleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#resume, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#intersectionObserver?.disconnect();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersection = (entries) => {
    const entry = entries[0];
    if (!entry) return;
    this.classList.toggle('at-partner-logos__carousel--offscreen', !entry.isIntersecting);
  };

  #syncMotionPreference = () => {
    this.classList.toggle('at-partner-logos__carousel--reduced-motion', this.#motionQuery.matches);
  };

  #pause = () => {
    this.classList.add('at-partner-logos__carousel--paused');
  };

  #resume = () => {
    this.classList.remove('at-partner-logos__carousel--paused');
  };
}

if (!customElements.get('at-partner-logos-marquee')) {
  customElements.define('at-partner-logos-marquee', PartnerLogosMarquee);
}
